import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store";
import {
  addStructureSystem,
  removeStructureSystem,
} from "../../../../store/features/optionalSystemsSlice";
import { StructureSystem } from "../../../../types/optionalSystems";
import { formatCurrency } from "../../../../utils/formatting";
import ProtectedSystemsSelector from "./ProtectedSystemsSelector";
import { PROTECTABLE_SHIP_SYSTEMS } from "../../../../constants/protectableShipSystems";
import { getSystemTonnageFromState } from "../../../../store/selectors/shipBuilderSelectors";
import { selectors } from "../../../../store/selectors";

interface StructureSystemsProps {
  availableSystems: any[];
  shipTonnage: number;
  techLevel: number;
  tlOverrideActive?: boolean;
}

const StructureSystems: React.FC<StructureSystemsProps> = ({
  availableSystems,
  shipTonnage,
  techLevel,
  tlOverrideActive = false,
}) => {
  const dispatch = useAppDispatch();
  const systems = useAppSelector((state) => state.optionalSystems.structure);
  const shipState = useAppSelector((state) => state.shipBuilder);

  // State for Armoured Bulkhead protected systems
  const [showProtectedSelector, setShowProtectedSelector] = useState(false);
  const [pendingBulkhead, setPendingBulkhead] =
    useState<StructureSystem | null>(null);
  const [armouredBulkheadProtected, setArmouredBulkheadProtected] = useState<
    string[]
  >([]);

  // Get assigned crew count (prefer overrides, else minimums)


  const getSystemTonnage = (id: string): number =>
    getSystemTonnageFromState(shipState, id);

  // Precompute selector results for all structure system types at the top level
  const pressureHullStats = (useAppSelector(
    selectors.derived.selectPressureHullStats,
  ) || {}) as any;
  const internalArmouryStats = (useAppSelector(
    selectors.derived.selectInternalArmouryStats,
  ) || {}) as any;

  // Precompute adjustable hull stats for each system individually at the top level
  const adjustableHullSystems = availableSystems.filter(
    (s) => s.subtype === "adjustableHull",
  );
  const adjustableHullStats1 = useAppSelector((state) =>
    selectors.derived.selectAdjustableHullStats(
      state,
      adjustableHullSystems[0] ?? null,
    ),
  );
  const adjustableHullStats2 = useAppSelector((state) =>
    selectors.derived.selectAdjustableHullStats(
      state,
      adjustableHullSystems[1] ?? null,
    ),
  );
  const adjustableHullStats: Record<string, any> = {};
  if (adjustableHullSystems[0])
    adjustableHullStats[adjustableHullSystems[0].id] = adjustableHullStats1;
  if (adjustableHullSystems[1])
    adjustableHullStats[adjustableHullSystems[1].id] = adjustableHullStats2;

  // Process available systems with dynamic calculations and validation
  const processedAvailableSystems = availableSystems.map((system) => {
    let calculatedCost = system.cost;
    let calculatedTonnage = system.tonnage;
    let calculatedPower = system.power;
    let validationErrors: string[] = [];
    let isInstalled = systems.some((s) => s.id === system.id);

    // Special handling for each structure system type
    if (system.subtype === "armouredBulkhead") {
      // Armoured Bulkhead calculations are handled when user selects protected systems
      calculatedCost = 0; // Will be calculated based on protected tonnage
      calculatedTonnage = 0; // Will be calculated as 10% of protected tonnage
      calculatedPower = 0;
    } else if (system.subtype === "adjustableHull") {
      const stats = adjustableHullStats[system.id];
      calculatedCost = stats.cost;
      calculatedTonnage = stats.tonnage;
      calculatedPower = stats.power;
    } else if (system.subtype === "pressureHull") {
      calculatedCost = pressureHullStats.cost;
      calculatedTonnage = pressureHullStats.tonnage;
      calculatedPower = pressureHullStats.power;
    } else if (system.subtype === "internalArmoury") {
      calculatedCost = internalArmouryStats.cost;
      calculatedTonnage = internalArmouryStats.tonnage;
      calculatedPower = internalArmouryStats.power;
    }

    // Tech level validation
    if (!tlOverrideActive && (system.techLevel ?? 0) > techLevel) {
      validationErrors.push(`Requires TL ${system.techLevel}`);
    }

    // Check for duplicate systems
    if (isInstalled) {
      validationErrors.push("This system is already installed");
    }

    return {
      ...system,
      cost: calculatedCost,
      tonnage: calculatedTonnage,
      power: calculatedPower,
      validationErrors,
      isInstalled,
    };
  });

  const handleAddSystem = (system: StructureSystem) => {
    let finalSystem = { ...system };

    if (system.subtype === "armouredBulkhead") {
      // For Armoured Bulkhead, we need to show the protected systems selector
      setPendingBulkhead(system);
      setShowProtectedSelector(true);
      return;
    } else if (system.subtype === "adjustableHull") {
      const stats = adjustableHullStats[system.id];
      finalSystem = {
        ...system,
        tonnage: stats.tonnage,
        cost: stats.cost,
        power: stats.power,
        properties: {
          ...system.properties,
          calculated: true,
        },
      };
    } else if (system.subtype === "pressureHull") {
      finalSystem = {
        ...system,
        tonnage: pressureHullStats.tonnage,
        cost: pressureHullStats.cost,
        power: pressureHullStats.power,
        properties: {
          ...system.properties,
          calculated: true,
        },
      };
    } else if (system.subtype === "internalArmoury") {
      finalSystem = {
        ...system,
        tonnage: internalArmouryStats.tonnage,
        cost: internalArmouryStats.cost,
        power: internalArmouryStats.power,
        properties: {
          ...system.properties,
          calculated: true,
        },
      };
    }

    dispatch(addStructureSystem(finalSystem));
  };

  const handleRemoveSystem = (systemId: string) => {
    dispatch(removeStructureSystem(systemId));
  };

  const handleConfirmProtection = (
    protectedIds: string[],
    protectedTonnage: number,
  ) => {
    if (pendingBulkhead) {
      // Calculate armoured bulkhead stats based on protected tonnage
      const bulkheadTonnage = protectedTonnage * 0.1; // 10% of protected tonnage
      const cost = protectedTonnage * 200000; // Cr200,000 per ton protected
      const finalSystem = {
        ...pendingBulkhead,
        protectedSystems: protectedIds,
        protectedTonnage,
        tonnage: bulkheadTonnage,
        cost: cost,
        properties: {
          ...pendingBulkhead.properties,
          calculated: true,
        },
      };
      dispatch(addStructureSystem(finalSystem));
      setArmouredBulkheadProtected(protectedIds);
      setPendingBulkhead(null);
      setShowProtectedSelector(false);
    }
  };

  const handleCancelProtection = () => {
    setShowProtectedSelector(false);
    setPendingBulkhead(null);
  };

  // Group adjustable hull systems for dropdown
  const adjustableHullOptions = processedAvailableSystems.filter(
    (s) => s.subtype === "adjustableHull",
  );
  const [selectedAdjustableHull, setSelectedAdjustableHull] = useState(
    adjustableHullOptions.length > 0 ? adjustableHullOptions[0].id : "",
  );
  const selectedAdjustableHullObj = adjustableHullOptions.find(
    (s) => s.id === selectedAdjustableHull,
  );

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Structure Systems</h2>
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-white dark:bg-gray-800">
        <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          Available Structure Systems
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
          {processedAvailableSystems
            .filter(
              (system) =>
                techLevel >= system.techLevel &&
                system.subtype !== "adjustableHull",
            )
            .map((system) => (
              <div
                key={system.id}
                className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 h-full border-gray-200 dark:border-gray-600"
              >
                <div>
                  <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    {system.name}
                  </div>

                  <ul className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                    <li>
                      <b>Cost:</b> {formatCurrency(system.cost)} Cr
                    </li>
                    <li>
                      <b>Tonnage:</b> {system.tonnage.toFixed(1)} tons
                    </li>
                    <li>
                      <b>Power:</b> {system.power}
                    </li>

                    {/* Special properties for each system type */}
                    {system.subtype === "armouredBulkhead" && (
                      <>
                        <li>
                          <b>Cost:</b>{" "}
                          {`${formatCurrency(200000)} per ton`}
                        </li>
                        <li>
                          <b>Tonnage:</b> 10% of protected system
                        </li>
                      </>
                    )}
                    {system.subtype === "adjustableHull" && (
                      <>
                        <li>
                          <b>Reconfiguration:</b> 1 round
                        </li>
                        <li>
                          <b>Pop-up Mounts:</b> All weapons included
                        </li>
                      </>
                    )}
                    {system.subtype === "pressureHull" && (
                      <>
                        <li>
                          <b>Pressure Resistance:</b> Gas giant deeps and underwater up to 50 km
                        </li>
                        <li>
                          <b>Armour Bonus:</b> +4
                        </li>
                        <li>
                          <b>Cost:</b> 10x normal hull cost
                        </li>
                        <li>
                          <b>Tonnage:</b> 25% of vessel tonnage
                        </li>
                      </>
                    )}
                  </ul>

                  {system.validationErrors.length > 0 && (
                    <div className="text-xs text-red-600 mb-2">
                      {system.validationErrors.map((err: string, idx: number) => (
                        <div key={idx}>{err}</div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-2 flex gap-2">
                  {!system.isInstalled ? (
                    <button
                      className={`w-full mt-2 px-3 py-1 rounded text-white font-semibold ${
                        system.validationErrors.length > 0
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700"
                      }`}
                      onClick={() => handleAddSystem(system as StructureSystem)}
                      disabled={system.validationErrors.length > 0}
                      title={
                        system.validationErrors.length > 0
                          ? system.validationErrors.join(" ")
                          : ""
                      }
                    >
                      Add
                    </button>
                  ) : (
                    <button
                      className="px-4 py-1 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                      onClick={() => handleRemoveSystem(system.id)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}

          {/* Adjustable Hull single card with dropdown */}
          {adjustableHullOptions.length > 0 && (
            <div className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 h-full border-gray-200 dark:border-gray-600">
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Adjustable Hull
                </div>
                <select
                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm mb-2 dark:bg-gray-600 dark:text-gray-100"
                  value={selectedAdjustableHull}
                  onChange={(e) => setSelectedAdjustableHull(e.target.value)}
                >
                  {adjustableHullOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.name}
                    </option>
                  ))}
                </select>
                <ul className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <li>
                    <b>Cost:</b>{" "}
                    {formatCurrency(selectedAdjustableHullObj?.cost ?? 0)} Cr
                  </li>
                  <li>
                    <b>Tonnage:</b>{" "}
                    {selectedAdjustableHullObj?.tonnage.toFixed(1)} tons
                  </li>
                  <li>
                    <b>Power:</b> {selectedAdjustableHullObj?.power}
                  </li>
                  <li>
                    <b>Reconfiguration:</b> 1 round
                  </li>
                  <li>
                    <b>Pop-up Mounts:</b> All weapons included
                  </li>
                </ul>
                {selectedAdjustableHullObj &&
                  selectedAdjustableHullObj.validationErrors &&
                  selectedAdjustableHullObj.validationErrors.length > 0 && (
                    <div className="text-xs text-red-600 mb-2">
                      {selectedAdjustableHullObj.validationErrors.map((err: string, idx: number) => (
                        <div key={idx}>{err}</div>
                      ))}
                    </div>
                  )}
              </div>
              <div className="mt-2 flex gap-2">
                {!selectedAdjustableHullObj?.isInstalled ? (
                  <button
                    className={`w-full mt-2 px-3 py-1 rounded text-white font-semibold ${
                      selectedAdjustableHullObj &&
                      selectedAdjustableHullObj.validationErrors &&
                      selectedAdjustableHullObj.validationErrors.length > 0
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                    onClick={() =>
                      handleAddSystem(
                        selectedAdjustableHullObj as StructureSystem,
                      )
                    }
                    disabled={
                      selectedAdjustableHullObj &&
                      selectedAdjustableHullObj.validationErrors &&
                      selectedAdjustableHullObj.validationErrors.length > 0
                    }
                    title={
                      selectedAdjustableHullObj?.validationErrors?.join(" ") ??
                      ""
                    }
                  >
                    Add
                  </button>
                ) : (
                  <button
                    className="px-4 py-1 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                    onClick={() =>
                      handleRemoveSystem(selectedAdjustableHullObj.id)
                    }
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Show ProtectedSystemsSelector for Armoured Bulkhead as a modal overlay */}
      {showProtectedSelector && pendingBulkhead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-3xl relative max-h-screen overflow-y-auto">
            <ProtectedSystemsSelector
              eligibleSystems={PROTECTABLE_SHIP_SYSTEMS}
              getSystemTonnage={getSystemTonnage}
              onConfirm={handleConfirmProtection}
              onCancel={handleCancelProtection}
              initialSelected={armouredBulkheadProtected}
            />
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-2xl font-bold"
              onClick={handleCancelProtection}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StructureSystems;
